import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { companyInfo, allWifiPlans, wifiTvPlans } from '../mock';
import { trackFormSubmission, trackButtonClick } from '../utils/analytics';
import useScrollReveal from '../hooks/useScrollReveal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  useScrollReveal();

  const [showForm, setShowForm] = useState(false);
  const [pincodeValid, setPincodeValid] = useState(null);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    pincode: '',
    preferred_plan: ''
  });

  const validPincodes = ['500017', '500061'];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name || formData.name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }
    const phoneDigits = formData.phone.replace(/[^0-9]/g, '');
    if (!phoneDigits || phoneDigits.length !== 10) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.address || formData.address.trim().length < 10) {
      newErrors.address = 'Address must be at least 10 characters';
    }
    if (!formData.pincode || formData.pincode.length !== 6) {
      newErrors.pincode = 'Pincode must be exactly 6 digits';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePincodeCheck = (pincode) => {
    setFormData(prev => ({ ...prev, pincode }));
    if (pincode.length === 6) {
      if (validPincodes.includes(pincode)) {
        setPincodeValid(true);
        setShowWaitlist(false);
      } else {
        setPincodeValid(false);
        setShowWaitlist(true);
      }
    } else {
      setPincodeValid(null);
      setShowWaitlist(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      trackFormSubmission('Customer Registration', false);
      return;
    }
    setIsSubmitting(true);

    const cleanPhone = formData.phone.replace(/[^0-9]/g, '');
    const cleanName = formData.name.trim();
    const cleanEmail = formData.email.trim();
    const cleanAddress = formData.address.trim();
    const cleanPincode = formData.pincode.trim();
    const cleanPlan = formData.preferred_plan || null;

    // Fire the existing internal endpoint in the background (Mongo + Sheets + Email)
    // We deliberately do NOT await/block on this — the webhook is the source of truth.
    axios
      .post(`${API}/customer-registration`, {
        name: cleanName,
        phone: cleanPhone,
        email: cleanEmail,
        address: cleanAddress,
        pincode: cleanPincode,
        preferred_plan: cleanPlan,
      })
      .catch((err) => {
        // Swallow — webhook is the system of record. Just log for diagnostics.
        // eslint-disable-next-line no-console
        console.warn('Internal registration mirror failed:', err?.message || err);
      });

    // Primary submission: operations console webhook
    try {
      const webhookResp = await axios.post(
        'https://renewal-command.emergent.host/api/leads/webhook',
        {
          customer_name: cleanName,
          contact_number: cleanPhone,
          email: cleanEmail,
          address: cleanAddress,
          pack_interested: cleanPlan || '',
          service_type: 'broadband',
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Lead-Secret': 'ssmvision-lead-2026',
          },
        }
      );

      if (webhookResp.status >= 200 && webhookResp.status < 300) {
        trackFormSubmission('Customer Registration', true);
        toast.success(
          showWaitlist
            ? "Thank you, we'll contact you shortly. (Added to waitlist for your area)"
            : "Thank you, we'll contact you shortly.",
          { duration: 5000 }
        );
        setFormData({ name: '', phone: '', email: '', address: '', pincode: '', preferred_plan: '' });
        setPincodeValid(null);
        setShowWaitlist(false);
        setShowForm(false);
        setErrors({});
      } else {
        throw new Error(`Unexpected status ${webhookResp.status}`);
      }
    } catch (error) {
      console.error('Registration error:', error);
      trackFormSubmission('Customer Registration', false);
      toast.error('Failed to submit registration. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const allPlans = [
    ...allWifiPlans.map(p => `${p.speed} WiFi`),
    ...wifiTvPlans.map(p => p.speed)
  ];

  const inputCls = (hasError) =>
    `bg-white/5 border ${hasError ? 'border-red-500/60' : 'border-white/15'} text-white placeholder:text-white/40 focus:border-orange-400 focus:ring-orange-400/30 backdrop-blur-md`;

  return (
    <div className="min-h-screen bg-[#050505] text-white py-16 relative overflow-hidden" data-testid="contact-page">
      <div className="ambient-glow" />
      <div className="grain-overlay" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-14 reveal-zoom">
          <Badge className="bg-orange-500/10 border border-orange-500/30 text-orange-300 mb-4">
            Get In Touch
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-4" data-testid="contact-heading">
            Let's Get You <span className="bg-gradient-to-r from-[#FFD27A] to-[#FF5C00] bg-clip-text text-transparent">Connected</span>
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Fill out the form below and our team will get back to you within 24 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Form Side */}
          {!showForm ? (
            <Card key="interested-card" className="glass-orange border-0 reveal-left" data-testid="contact-interested-card">
              <CardHeader className="border-b border-white/10">
                <CardTitle className="text-2xl text-white">Interested in Our Services?</CardTitle>
              </CardHeader>
              <CardContent className="p-10 text-center">
                <p className="text-white/70 mb-8">
                  Click below to fill out our registration form and we'll get back to you within 24 hours.
                </p>
                <Button
                  onClick={() => {
                    setShowForm(true);
                    trackButtonClick('Yes, I\'m Interested!', 'Contact Page');
                  }}
                  className="bg-gradient-to-r from-[#FF8C00] to-[#FFB800] hover:from-[#FF7700] hover:to-[#FFA600] text-black font-semibold px-10 py-6 text-lg shadow-[0_8px_32px_-8px_rgba(255,140,0,0.7)] hover:scale-105 transition-transform"
                  size="lg"
                  data-testid="contact-interested-btn"
                >
                  Yes, I'm Interested!
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card key="form-card" className="glass border-0 reveal-left" data-testid="contact-form-card">
              <CardHeader className="border-b border-white/10">
                <CardTitle className="text-2xl text-white">Request a Connection</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="space-y-2">
                    <Label htmlFor="pincode" className="text-white/80">Service Area Pincode *</Label>
                    <Input
                      id="pincode"
                      type="text"
                      placeholder="Enter your pincode"
                      value={formData.pincode}
                      onChange={(e) => handlePincodeCheck(e.target.value)}
                      maxLength={6}
                      required
                      className={inputCls(errors.pincode)}
                      data-testid="contact-pincode-input"
                    />
                    {errors.pincode && <p className="text-red-400 text-sm">{errors.pincode}</p>}
                    {pincodeValid === true && (
                      <div className="flex items-center gap-2 text-emerald-400 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        <span>Service available in your area!</span>
                      </div>
                    )}
                    {pincodeValid === false && (
                      <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 mt-2 backdrop-blur-sm">
                        <div className="flex items-start gap-2 text-amber-200">
                          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <p className="font-semibold mb-1">Sorry, we're not serving this area yet.</p>
                            <p>We currently serve pincodes: <strong>500017, 500061</strong></p>
                            <p className="mt-2">Add your details below — once we have enough requests we'll expand to your area.</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white/80">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                      className={inputCls(errors.name)}
                      data-testid="contact-name-input"
                    />
                    {errors.name && <p className="text-red-400 text-sm">{errors.name}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-white/80">Phone Number * (10 digits)</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="Enter your 10-digit phone number"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      required
                      maxLength={10}
                      className={inputCls(errors.phone)}
                      data-testid="contact-phone-input"
                    />
                    {errors.phone && <p className="text-red-400 text-sm">{errors.phone}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white/80">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                      className={inputCls(errors.email)}
                      data-testid="contact-email-input"
                    />
                    {errors.email && <p className="text-red-400 text-sm">{errors.email}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-white/80">Installation Address *</Label>
                    <Textarea
                      id="address"
                      placeholder="Enter your complete address (min 10 characters)"
                      value={formData.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      rows={3}
                      required
                      className={inputCls(errors.address)}
                      data-testid="contact-address-input"
                    />
                    {errors.address && <p className="text-red-400 text-sm">{errors.address}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="plan" className="text-white/80">Preferred Plan</Label>
                    <Select value={formData.preferred_plan} onValueChange={(value) => handleChange('preferred_plan', value)}>
                      <SelectTrigger className={inputCls(false)} data-testid="contact-plan-select">
                        <SelectValue placeholder="Select a plan" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0f0f0f] border-white/10 text-white">
                        {allPlans.map((plan, idx) => (
                          <SelectItem key={idx} value={plan} className="focus:bg-orange-500/20 focus:text-white">
                            {plan}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-[#FF8C00] to-[#FFB800] hover:from-[#FF7700] hover:to-[#FFA600] text-black font-semibold text-lg py-6 shadow-[0_8px_32px_-8px_rgba(255,140,0,0.7)]"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? 'Submitting...' : (showWaitlist ? 'Join Waitlist' : 'Submit Request')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Info Side */}
          <div className="space-y-6">
            <Card className="glass border-0 reveal-right" data-testid="contact-info-card">
              <CardHeader className="border-b border-white/10">
                <CardTitle className="text-2xl text-white">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {[
                  { icon: Phone, title: 'Phone', value: companyInfo.phone, href: `tel:${companyInfo.phone}`, sub: 'Call us anytime' },
                  { icon: Mail, title: 'Email', value: companyInfo.email, href: `mailto:${companyInfo.email}`, sub: 'We respond within 24 hours' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF8C00] to-[#FF5C00] flex items-center justify-center flex-shrink-0 shadow-[0_8px_24px_-8px_rgba(255,140,0,0.7)]">
                      <item.icon className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                      <a href={item.href} className="text-[#FFB347] hover:underline text-lg">
                        {item.value}
                      </a>
                      <p className="text-sm text-white/50 mt-1">{item.sub}</p>
                    </div>
                  </div>
                ))}

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF8C00] to-[#FF5C00] flex items-center justify-center flex-shrink-0 shadow-[0_8px_24px_-8px_rgba(255,140,0,0.7)]">
                    <MapPin className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Office Address</h3>
                    <p className="text-white/70">{companyInfo.address}</p>
                    <p className="text-sm text-white/50 mt-2 font-semibold">Service Areas:</p>
                    <p className="text-sm text-white/50">Pincodes: 500017, 500061</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF8C00] to-[#FF5C00] flex items-center justify-center flex-shrink-0 shadow-[0_8px_24px_-8px_rgba(255,140,0,0.7)]">
                    <Clock className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Business Hours</h3>
                    <p className="text-white/70">Monday – Sunday</p>
                    <p className="text-white/70">9:00 AM – 8:00 PM</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-orange border-0 reveal-right reveal-d2" data-testid="contact-quick-install">
              <CardContent className="p-6">
                <h3 className="text-2xl font-bold text-white mb-4">Quick Installation</h3>
                <p className="text-white/80 mb-4">Get connected within 24-48 hours of submitting your request.</p>
                <ul className="space-y-2 text-white/80">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-300 rounded-full" />
                    <span>Free installation</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-300 rounded-full" />
                    <span>No hidden charges</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-300 rounded-full" />
                    <span>Professional technicians</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="glass border-0 reveal-right reveal-d3" data-testid="contact-whatsapp-card">
              <CardContent className="p-6 text-center">
                <h3 className="text-2xl font-bold text-white mb-4">Know More on WhatsApp</h3>
                <div className="bg-white p-3 rounded-xl inline-block mb-4">
                  <img
                    src="https://customer-assets.emergentagent.com/job_webfiber/artifacts/5ymmdmrq_image.png"
                    alt="WhatsApp QR Code"
                    className="w-48 h-48 mx-auto"
                  />
                </div>
                <p className="text-white/60">Scan to chat with us on WhatsApp</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
